import React, { forwardRef } from 'react';
import RawMuiButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material/styles';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Bouton du Design System V2 (Tableau de bord de mission — TEC-3051).
 *
 * Valeurs exactes de la maquette (`.btn-primary` / `.btn-outline` / `.btn-danger-ghost`) :
 * primaire bleu #1561EB radius 10 + glow `sh-primary` (le glow disparaît au survol),
 * secondaire blanc bordure g200 texte g600, danger « ghost » rouge doux. Pas de gradient,
 * pas de translate au survol. Composant autonome (le `Button` historique reste inchangé).
 */
export type ButtonV2Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
export type ButtonV2Size = 'small' | 'medium' | 'large';

export type ButtonV2Props = {
  children: React.ReactNode;
  variant?: ButtonV2Variant;
  size?: ButtonV2Size;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'submit' | 'button' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  /** Bouton carré centré sur une icône, sans libellé (combine avec `secondary`/`ghost`). */
  iconOnly?: boolean;
  /** État sélectionné/pressé (toggle) : pose `aria-pressed` et applique le style actif. */
  active?: boolean;
  sx?: SxProps<Theme>;
  id?: string;
  title?: string;
  ariaLabel?: string;
};

const sizeStyles = (size: ButtonV2Size = 'medium') => {
  switch (size) {
    case 'small':
      return { height: 32, padding: '0 14px', fontSize: 12 };
    case 'large':
      return { height: 44, padding: '0 20px', fontSize: 14 };
    case 'medium':
    default:
      return { height: 38, padding: '0 16px', fontSize: 13 };
  }
};

const StyledButtonV2 = styled(RawMuiButton, {
  shouldForwardProp: (prop) => !['dsVariant', 'dsSize', 'dsIconOnly', 'dsActive'].includes(prop as string),
})<{ dsVariant: ButtonV2Variant; dsSize: ButtonV2Size; dsIconOnly?: boolean; dsActive?: boolean }>(
  ({ dsVariant, dsSize, dsIconOnly, dsActive }) => {
    const { height, padding, fontSize } = sizeStyles(dsSize);
    const isLink = dsVariant === 'link';
    const base = {
      height: isLink ? 'auto' : height,
      minWidth: isLink || dsIconOnly ? 0 : undefined,
      width: dsIconOnly && !isLink ? height : undefined,
      padding: isLink || dsIconOnly ? 0 : padding,
      fontSize,
      gap: dsIconOnly ? 0 : 8,
      borderRadius: isLink ? 0 : dsV2.radius.md,
      textTransform: 'none' as const,
      fontFamily: dsV2.font.sansSemiBold,
      fontWeight: 600,
      lineHeight: 1.2,
      border: '1px solid transparent',
      boxShadow: 'none',
      transition: 'background-color .15s ease, box-shadow .15s ease, border-color .15s ease, color .15s ease',
      '&.Mui-disabled': {
        backgroundColor: isLink || dsVariant === 'ghost' ? 'transparent' : dsV2.color.g100,
        color: dsV2.color.g300,
        borderColor: 'transparent',
        boxShadow: 'none',
      },
    };

    if (isLink) {
      return {
        ...base,
        backgroundColor: 'transparent',
        color: dsV2.color.bleu,
        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
      };
    }

    if (dsVariant === 'ghost') {
      return {
        ...base,
        backgroundColor: dsActive ? dsV2.color.bleuDoux : dsV2.color.bleuDouxLight,
        color: dsV2.color.bleu,
        borderColor: dsActive ? dsV2.color.bleuBrd : 'transparent',
        '&:hover': { backgroundColor: dsV2.color.bleuDoux, boxShadow: 'none' },
      };
    }

    if (dsVariant === 'secondary') {
      return {
        ...base,
        backgroundColor: dsActive ? dsV2.color.bleuDim : dsV2.color.blanc,
        color: dsActive ? dsV2.color.bleu : dsV2.color.g600,
        borderColor: dsActive ? dsV2.color.bleu : dsV2.color.g200,
        '&:hover': { backgroundColor: dsActive ? dsV2.color.bleuDim : dsV2.color.g50, boxShadow: 'none' },
      };
    }

    if (dsVariant === 'danger') {
      return {
        ...base,
        backgroundColor: dsV2.color.rougeLight,
        color: dsV2.color.rougeDark,
        '&:hover': { backgroundColor: dsV2.color.rougeBorder, boxShadow: 'none' },
      };
    }

    // primary
    return {
      ...base,
      backgroundColor: dsV2.color.bleu,
      color: dsV2.color.blanc,
      boxShadow: dsV2.shadow.primary,
      '&:hover': { backgroundColor: dsV2.color.bleuDark, boxShadow: 'none' },
    };
  },
);

const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      onClick,
      type = 'button',
      disabled,
      loading,
      startIcon,
      endIcon,
      fullWidth,
      iconOnly,
      active,
      sx,
      id,
      title,
      ariaLabel,
    },
    ref,
  ) => {
    return (
      <StyledButtonV2
        ref={ref}
        id={id}
        title={title}
        aria-label={ariaLabel}
        aria-pressed={typeof active === 'boolean' ? active : undefined}
        type={type}
        onClick={onClick}
        disabled={disabled}
        loading={loading}
        loadingPosition={loading ? 'end' : undefined}
        startIcon={startIcon}
        endIcon={endIcon}
        fullWidth={fullWidth}
        disableRipple
        dsVariant={variant}
        dsSize={size}
        dsIconOnly={iconOnly}
        dsActive={active}
        sx={sx}
      >
        {children}
      </StyledButtonV2>
    );
  },
);

ButtonV2.displayName = 'ButtonV2';

export default ButtonV2;
export { ButtonV2 };
